import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  CircularProgress,
  Alert,
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Chip,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Paper
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  AccountBalance as AccountIcon,
  TrendingUp as TrendingUpIcon,
  Receipt as ReceiptIcon,
  Memory as MemoryIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import api from '../../utils/api';
import { formatDistanceToNow } from 'date-fns';

const AIUsageModal = ({ open, onClose, userEmail, userId }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [usageData, setUsageData] = useState(null);

  useEffect(() => {
    if (open && (userEmail || userId)) {
      loadUsageData();
    } else {
      setUsageData(null);
      setError(null);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- verified intentional deps
  }, [open, userEmail, userId]);

  const loadUsageData = async () => {
    setLoading(true);
    setError(null);
    setUsageData(null);

    try {
      let response;
      // Prefer userId if available, as it's more reliable
      if (userId) {
        response = await api.get(`/api/admin/ai-usage-by-id/${userId}`);
      } else if (userEmail) {
        // Use query parameter instead of path parameter for email to avoid encoding issues
        response = await api.get(`/api/admin/ai-usage`, {
          params: { email: userEmail }
        });
      } else {
        throw new Error('Either userEmail or userId must be provided');
      }
      
      setUsageData(response.data);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch AI usage data');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="lg" 
      fullWidth
      PaperProps={{
        sx: { maxHeight: '90vh' }
      }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">AI Usage Details</Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        ) : usageData ? (
          <Box>
            {/* User Info */}
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  User Information
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mt: 2 }}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Name
                    </Typography>
                    <Typography variant="body1">
                      {usageData.user.firstName} {usageData.user.lastName}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Email
                    </Typography>
                    <Typography variant="body1">{usageData.user.email}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Role
                    </Typography>
                    <Chip
                      label={usageData.user.role}
                      size="small"
                      color={usageData.user.role === 'supplier' ? 'primary' : 'default'}
                    />
                  </Box>
                </Box>
              </CardContent>
            </Card>

            {/* Summary Stats */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} sm={6} md={3}>
                <Card>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <TrendingUpIcon color="primary" fontSize="small" />
                      <Typography variant="caption" color="text.secondary">
                        Total Requests
                      </Typography>
                    </Box>
                    <Typography variant="h5">
                      {usageData.summary.totalRequests.toLocaleString()}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <MemoryIcon color="primary" fontSize="small" />
                      <Typography variant="caption" color="text.secondary">
                        Total Tokens
                      </Typography>
                    </Box>
                    <Typography variant="h5">
                      {usageData.summary.totalTokensUsed.toLocaleString()}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <AccountIcon color="primary" fontSize="small" />
                      <Typography variant="caption" color="text.secondary">
                        Credits Used
                      </Typography>
                    </Box>
                    <Typography variant="h5">
                      {usageData.summary.totalCreditsUsed.toFixed(2)}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <ReceiptIcon color="primary" fontSize="small" />
                      <Typography variant="caption" color="text.secondary">
                        Total Cost
                      </Typography>
                    </Box>
                    <Typography variant="h5" color="primary">
                      £{usageData.summary.totalCostGBP.toFixed(2)}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            {/* Detailed Usage by Wedding/Business */}
            {usageData.details.length > 0 && (
              <Box>
                <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
                  Usage by {usageData.user.role === 'supplier' ? 'Business' : 'Wedding'}
                </Typography>
                {usageData.details.map((detail, index) => (
                  <Accordion key={index} sx={{ mb: 2 }}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', pr: 2 }}>
                        <Box>
                          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                            {detail.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {detail.requestCount} requests • {detail.creditsUsed.toFixed(2)} credits • £{(detail.creditsUsed / 100).toFixed(2)}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Chip
                            label={`${detail.remainingCredits.toFixed(2)} remaining`}
                            size="small"
                            color={detail.remainingCredits < 50 ? 'warning' : 'success'}
                          />
                        </Box>
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Grid container spacing={2} sx={{ mb: 2 }}>
                        <Grid item xs={12} sm={6}>
                          <Typography variant="caption" color="text.secondary">
                            Remaining Credits
                          </Typography>
                          <Typography variant="body1">{detail.remainingCredits.toFixed(2)}</Typography>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Typography variant="caption" color="text.secondary">
                            Total Used (All Time)
                          </Typography>
                          <Typography variant="body1">{detail.totalUsed.toFixed(2)}</Typography>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Typography variant="caption" color="text.secondary">
                            Tokens Used
                          </Typography>
                          <Typography variant="body1">{detail.tokensUsed.toLocaleString()}</Typography>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Typography variant="caption" color="text.secondary">
                            Cost
                          </Typography>
                          <Typography variant="body1" color="primary">
                            £{(detail.creditsUsed / 100).toFixed(2)}
                          </Typography>
                        </Grid>
                      </Grid>
                      <Divider sx={{ my: 2 }} />
                      {detail.usageLogs.length > 0 ? (
                        <Box>
                          <Typography variant="subtitle2" gutterBottom>
                            Recent Usage Logs ({detail.usageLogs.length} total)
                          </Typography>
                          <TableContainer>
                            <Table size="small">
                              <TableHead>
                                <TableRow>
                                  <TableCell>Date</TableCell>
                                  <TableCell>Credits</TableCell>
                                  <TableCell>Tokens</TableCell>
                                  <TableCell>Model</TableCell>
                                  <TableCell>Endpoint</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {detail.usageLogs.slice(0, 10).map((log, logIndex) => (
                                  <TableRow key={logIndex}>
                                    <TableCell>
                                      {formatDistanceToNow(new Date(log.timestamp), { addSuffix: true })}
                                    </TableCell>
                                    <TableCell>{log.creditsDeducted.toFixed(2)}</TableCell>
                                    <TableCell>{log.tokensUsed.toLocaleString()}</TableCell>
                                    <TableCell>
                                      <Chip label={log.model} size="small" />
                                    </TableCell>
                                    <TableCell>
                                      <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                                        {log.endpoint}
                                      </Typography>
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </TableContainer>
                          {detail.usageLogs.length > 10 && (
                            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                              Showing 10 of {detail.usageLogs.length} usage logs
                            </Typography>
                          )}
                        </Box>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          No usage logs found
                        </Typography>
                      )}
                    </AccordionDetails>
                  </Accordion>
                ))}
              </Box>
            )}

            {usageData.details.length === 0 && (
              <Paper sx={{ p: 3, textAlign: 'center' }}>
                <Typography variant="body1" color="text.secondary">
                  No AI usage found for this user
                </Typography>
              </Paper>
            )}
          </Box>
        ) : null}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default AIUsageModal;

